import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import './CreatePodModal.css';

interface CreatePodModalProps {
  onClose: () => void;
  onCreate: (name: string, emails: string[]) => Promise<void>;
}

const CreatePodModal: React.FC<CreatePodModalProps> = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (emails.includes(email)) return;
    setEmails([...emails, email]);
    setEmailInput('');
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter(e => e !== email));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Give your Pod a name');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      await onCreate(name.trim(), emails);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create Pod');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cpod-overlay" onClick={onClose}>
      <div className="cpod-sheet" onClick={e => e.stopPropagation()}>
        <div className="cpod-handle" />

        <div className="cpod-header">
          <h2 className="cpod-title">Create a Pod</h2>
          <button className="cpod-close" onClick={onClose}><X size={18} /></button>
        </div>

        <p className="cpod-desc">A Pod is the group you plan with. Create one for your family, friends, or anyone you share plans with.</p>

        <label className="cpod-label">Pod name</label>
        <input
          className="cpod-input"
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="The Kim Family, Brunch Crew, Us Two..."
          maxLength={50}
          autoFocus
        />

        <label className="cpod-label">Invite members <span className="cpod-optional">(optional)</span></label>
        <div className="cpod-email-row">
          <input
            className="cpod-input cpod-email-input"
            type="email"
            value={emailInput}
            onChange={e => setEmailInput(e.target.value)}
            placeholder="Email address"
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())}
          />
          <button className="cpod-add-btn" onClick={addEmail} disabled={!emailInput.includes('@')}>
            <Plus size={16} />
          </button>
        </div>

        {emails.length > 0 && (
          <div className="cpod-emails">
            {emails.map(email => (
              <div key={email} className="cpod-email-chip">
                <span>{email}</span>
                <button className="cpod-email-remove" onClick={() => removeEmail(email)}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {error && <p className="cpod-error">{error}</p>}

        <button
          className="cpod-submit"
          onClick={handleSubmit}
          disabled={isSubmitting || !name.trim()}
        >
          {isSubmitting ? 'Creating...' : 'Create Pod'}
        </button>
      </div>
    </div>
  );
};

export default CreatePodModal;
